import React, {forwardRef, Ref} from "react";
import {Box, Button, ButtonProps, inputAdornmentClasses, sliderClasses} from "@mui/material";
import {MuiColorInput, MuiColorInputProps} from "mui-color-input";
import {Field} from "react-final-form";
import {MuiRFF, useFormV2} from "../../form";
import {styled} from "@mui/material/styles";

interface MuiColorTextFieldProps extends Omit<MuiColorInputProps, 'onChange' | 'value'> {
    form: false;
    value?: string;
    onChange?: (value?: string) => void;
    getAlternateColor?: (color?: string) => string | undefined;
}

interface FormColorTextFieldProps extends Omit<MuiRFF.TextFieldProps, 'onChange' | 'value'> {
    form: true;
    format?: MuiColorInputProps['format'];
    getAlternateColor?: (color?: string) => string | undefined;
}


type ColorTextFieldTypes = "MuiRFF" | "Mui";

export type ColorTextFieldProps<T extends ColorTextFieldTypes = "Mui"> =
    T extends "MuiRFF"
        ? FormColorTextFieldProps
        : T extends "Mui"
            ? MuiColorTextFieldProps
            : never;

const _ColorTextField = <T extends ColorTextFieldTypes = "Mui">(props: ColorTextFieldProps<T>, ref: Ref<HTMLInputElement>) => {
    const {values, form} = useFormV2() ?? {};
    const {getAlternateColor} = props;

    const valueFromProps = React.useMemo(() => props.form ? values?.[props.name] : props.value, [values, props]);
    const [color, setColor] = React.useState<string | undefined>(valueFromProps);
    const alternateColor = React.useMemo(() => getAlternateColor?.(color), [color, getAlternateColor]);

    /**
     * Commits the value of the color input.
     */
    const commitValue = React.useCallback((e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (props.form) {
            form?.change(props.name, color)
        } else {
            props.onChange?.(color)
        }
        if (props.onBlur) {
            props.onBlur(e);
        }
    }, [color, form, props])

    /**
     * Updates the color when the color changes.
     */
    React.useEffect(() => {
        setColor(valueFromProps);
    }, [valueFromProps]);

    const InputProps = React.useMemo(() => ({
        sx: {
            [`.${inputAdornmentClasses.root}`]: {
                marginLeft: -1,
            },
            [`.${inputAdornmentClasses.positionStart}`]: {
                width: '100%',
                maxWidth: 50,
                height: '100%',
                maxHeight: '100%',
                cursor: 'pointer',
            },
        }
    }), [])

    const PopoverProps = React.useMemo(() => ({
        sx: {
            "& .MuiColorInput-PopoverBody": {
                "& .MuiColorInput-ColorSpace": {
                    willChange: 'background-color',
                },
                [`& .${sliderClasses.rail}`]: {
                    willChange: 'background',
                },
            },
        },
    }), [])

    if (props.form) {

        const {form, getAlternateColor: _, ...rest} = props as FormColorTextFieldProps;

        return (
            <Field
                name={props.name}
                render={({input, meta}) => {
                    const isError = MuiRFF.showErrorOnChange({meta}) || (meta.submitFailed && meta.error);
                    return (
                        <MuiColorInput
                            {...rest}
                            ref={ref}
                            value={color ?? ''}
                            fallbackValue={'transparent'}
                            adornmentPosition={'start'}
                            format={props.format ?? "hex8"}
                            onChange={setColor}
                            onBlur={(e) => {
                                input.onChange(e);
                                input.onBlur(e);
                                commitValue(e);
                            }}
                            Adornment={(props) => <ColorInputColorVisualizer {...props} alternateColor={alternateColor}/>}
                            error={isError}
                            helperText={isError ? (meta.error || meta.submitError) : props.helperText}
                            sx={{width: '100%', ...props.sx}}
                            InputProps={InputProps}
                            PopoverProps={PopoverProps}
                        />
                    );
                }}
            />
        );
    } else {
        const {value, onChange, form, getAlternateColor: _, ...rest} = props as MuiColorTextFieldProps;
        return (
            <Box component={'span'} sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%'}}>
                <MuiColorInput
                    {...rest}
                    ref={ref}
                    value={color ?? ''}
                    fallbackValue={'transparent'}
                    adornmentPosition={'start'}
                    format={props.format ?? "hex8"}
                    onChange={setColor}
                    onBlur={commitValue}
                    Adornment={(props) => <ColorInputColorVisualizer {...props} alternateColor={alternateColor}/>}
                    sx={{width: '100%', ...props.sx}}
                    InputProps={InputProps}
                    PopoverProps={PopoverProps}
                />
            </Box>
        );
    }
}

const ColorTextField = forwardRef(_ColorTextField);

export default ColorTextField;

const SpanButton = (props: ButtonProps<'span'>) => <Button {...props} component={'span'}/>;
const StyledSpanButton = styled(SpanButton)(({theme, about}) => {
    const aboutParsed = typeof about === 'string' ? JSON.parse(about) : about;
    const color = aboutParsed?.color || 'transparent';
    const alternateColor = aboutParsed?.alternateColor;

    return ({
        width: '100%',
        minWidth: 50,
        height: 42,
        borderRadius: theme.shape.borderRadius,
        flexShrink: 0,
        flexGrow: 1,
        border: color,
        backgroundColor: color,
        transition: theme.transitions.create(['background-color', 'border-color'], {
            duration: theme.transitions.duration.complex,
        }),
        willChange: 'background-color, border-color',
        "&:hover": {
            backgroundColor: color,
        },
        "&:disabled": {
            border: color,
            backgroundColor: color,
        },
        ...(!!alternateColor && {
            // Triangle that sits on top of the main color on its bottom right corner, showing the alternate color.
            "&:before": {
                content: '""',
                position: 'absolute',
                top: 0,
                right: 0,
                width: 0,
                height: 0,
                borderStyle: 'solid',
                borderRadius: theme.shape.borderRadius,
                borderWidth: '0 0 42px 50px',
                borderColor: `transparent transparent ${alternateColor} transparent`,
            },
        })
    })
})


type AdornmentProps = Parameters<Required<MuiColorInputProps>['Adornment']>[0];

export interface ColorInputColorVisualizerProps extends AdornmentProps {
    alternateColor?: string;
}

export const ColorInputColorVisualizer: React.FC<ColorInputColorVisualizerProps> = (props) => {
    const {bgColor, isBgColorValid, disablePopover, alternateColor, ...rest} = props;

    const color = `${bgColor || 'transparent'} !important`;
    return (
        <>
            <StyledSpanButton {...rest} about={JSON.stringify({color, alternateColor})}/>
        </>
    );
}
